import { useRef, useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { BryntumSchedulerPro } from '@bryntum/schedulerpro-react-thin';
import { mapLocaleToBryntumLocale } from '../../../../../utils/bryntumSchedulerProLocale';

type SchedulerRef = MutableRefObject<BryntumSchedulerPro | null>;

type Props = {
  locale: string;
  resources: unknown[];
  initialLoading: boolean;
  startDate: Date;
  endDate: Date;
};

export function useSchedulerSetup(
  schedulerRef: SchedulerRef,
  { locale, resources, initialLoading, startDate, endDate }: Props,
): void {
  // Always-current snapshot of the query date range, used by the
  // one-time initial span effect without listing it as a dependency.
  const dateRangeRef = useRef({ startDate, endDate });
  useEffect(() => {
    dateRangeRef.current = { startDate, endDate };
  }, [startDate, endDate]);

  // load the scheduler with the employee's locale
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance;
    if (scheduler) {
      scheduler.localeManager.locale = mapLocaleToBryntumLocale(locale);
    }
  }, [locale, schedulerRef]);

  useEffect(() => {
    const scheduler = schedulerRef.current?.instance;
    if (!scheduler) {
      return;
    }
    scheduler.resourceStore.sort('name', true);
    const { startDate, endDate } = dateRangeRef.current;
    scheduler.setTimeSpan(startDate, endDate);
  }, [resources, schedulerRef]);

  // Apply the URL-derived date once, when the scheduler first becomes available.
  const initialTimeSpanSetRef = useRef(false);
  useEffect(() => {
    if (initialLoading || initialTimeSpanSetRef.current) return;
    initialTimeSpanSetRef.current = true;
    const { startDate, endDate } = dateRangeRef.current;
    schedulerRef.current?.instance?.setTimeSpan(startDate, endDate);
  }, [initialLoading, schedulerRef]);
}
